import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { FormNext } from 'grommet-icons';
import { AppRadioButton } from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { ProductFormCheckBoxInput } from '@Components/Partial/Product/NewSearch';
import {
  StyledServiceOptionContainer,
  StyledAccountTypeOption,
  StyledUppercaseText,
  StyledResponsiveContainer,
} from '../StyledOnboardingComponents';

const SERVICE_OPTIONS = [
  {
    key: 'useCid',
    label: 'YouTube Content ID',
    description: 'Recover lost revenue and protect your content on YouTube.',
  },
  {
    key: 'useSocial',
    label: 'Cyclops AI - Social Monitoring and Enforcement',
    description: 'Protect your content beyond YouTube. Monitor and enforce across social media.',
  },
  {
    key: 'useWatermarking',
    label: 'Cyclops AI - Watermarking',
    description: 'Capture leaks and unauthorized reuploads with invisible watermarks.',
  },
];

const ACCOUNT_TYPE_OPTIONS = [
  {
    key: 'creator',
    label: 'Individual',
    description: 'Manage up to 2 YouTube channels',
    features: [
      'Ideal for creators and small teams',
      'Full Content ID protection',
      'Revenue collection on matched uploads',
    ],
  },
  {
    key: 'agency',
    label: 'Agency',
    description: 'Manage up to 100 YouTube channels',
    features: [
      'Built for agencies and multi-channel networks',
      'Centralized dashboard for all clients',
      'Scalable protection and monetization',
    ],
  },
];

const ServiceSelectionStep = ({
  small,
  offerings,
  setOfferings,
  accountKind,
  setAccountKind,
}) => {
  const { theme: { page, button } } = useAppTheme();

  const renderServiceOptions = () => SERVICE_OPTIONS.map((option) => {
    const active = offerings[option.key];
    return (
      <StyledServiceOptionContainer
        key={option.key}
        active={active}
        highlightColor={button.primaryBg}
        hoverBg={button.selectBg}
        tabIndex={0}
        round="var(--app-radius)"
        pad="0.65rem"
        width="100%"
        onClick={() => (
          setOfferings({ ...offerings, [option.key]: !offerings[option.key] })
        )}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setOfferings({ ...offerings, [option.key]: !offerings[option.key] });
          }
        }}
      >
        <Box direction="row" gap="1rem" align="center" justify="start">
          <Box width="fit-content" flex={false}>
            <ProductFormCheckBoxInput
              controlled
              borderColor={active ? button.primaryBg : button.border}
              focusHighlight={page.focusHighlight}
              checkmarkColor={button.primaryBg}
              handleFormValues={(checked) => (
                setOfferings({ ...offerings, [option.key]: checked })
              )}
              value={active}
              label={null}
              customBoxSize={small ? '18px' : '30px'}
              customCheckmarkSize={small ? '18px' : '30px'}
            />
          </Box>
          <Box gap="0.25rem">
            <Text size="1rem" weight={500} color={page.primaryText}>
              {option.label}
            </Text>
            <Text size="0.85rem" color={page.secondaryText}>
              {option.description}
            </Text>
          </Box>
        </Box>
      </StyledServiceOptionContainer>
    );
  });

  const renderAccountTypeOptions = () => ACCOUNT_TYPE_OPTIONS.map((option) => {
    const active = accountKind === option.key;
    return (
      <StyledResponsiveContainer
        key={option.key}
        basis={small ? '100%' : '48%'}
      >
        <StyledAccountTypeOption
          active={active}
          highlightColor={button.primaryBg}
          hoverBg={button.selectBg}
          tabIndex={0}
          onClick={() => setAccountKind(option.key)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setAccountKind(option.key);
          }}
          margin={{ bottom: small ? 'small' : undefined }}
          flex="grow"
          round="var(--app-radius)"
          pad="0.65rem"
        >
          <Box direction="row" align="start" gap="1rem">
            <Box pad={{ top: '0.15rem' }}>
              <AppRadioButton
                name="account-type"
                checked={active}
                onChange={() => setAccountKind(option.key)}
                color={page.primaryText}
                highlightColor={button.primaryBg}
                borderColor={active ? button.primaryBg : button.border}
                label={null}
                customCircleSize={small ? '18px' : '24px'}
              />
            </Box>
            <Box gap="0.65rem">
              <Box direction={small ? 'column' : 'row'} gap="0.25rem" align={small ? 'start' : 'center'}>
                <Text size="1.1rem" weight={700} color={page.primaryText}>
                  {option.label}
                </Text>
                <Text size="1rem" color={page.secondaryText}>
                  {!small && <span>-&nbsp;</span>}
                  {option.description}
                </Text>
              </Box>
              <Box gap="0.5rem">
                {option.features.map((feature) => (
                  <Box key={feature} direction="row" gap="0.5rem" align="center">
                    <FormNext size="1rem" color="var(--page-alt-highlight)" />
                    <Text size="1rem" color={page.primaryText} weight={500}>
                      {feature}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </StyledAccountTypeOption>
      </StyledResponsiveContainer>
    );
  });

  return (
    <Box gap="1rem">
      <Box gap="0.25rem">
        <Text weight={600} size="1.25rem" color={page.primaryText}>Service Selection</Text>
        <Text size="0.85rem" color={page.secondaryText}>
          Pick what you need now. You can add more later.
        </Text>
      </Box>
      <Box
        round="var(--app-radius)"
        pad="1rem"
        background={page.cardBg}
        gap="0.5rem"
      >
        <Box gap="0.25rem" margin={{ bottom: '0.5rem' }}>
          <Text size="1rem" color={page.primaryText} weight={600}>
            Choose one or more services that suit your use case
          </Text>
        </Box>
        <Box gap="0.5rem">
          {renderServiceOptions()}
        </Box>
      </Box>
      <Box
        round="var(--app-radius)"
        pad="1rem"
        background={page.cardBg}
        gap="0.5rem"
      >
        <Box
          direction={small ? 'column' : 'row'}
          justify="between"
          align={small ? 'start' : 'center'}
          gap="0.5rem"
        >
          <Text size="1rem" color={page.primaryText} weight={600}>
            Select the account type that fits your Content ID management needs
          </Text>
          <Box
            pad={{ vertical: '0.3rem', horizontal: '0.5rem' }}
            round="var(--app-radius)"
            background={button.selectBg}
          >
            <StyledUppercaseText uppercase size="1rem" weight={700} color={page.altHighlight}>
              {accountKind === 'creator' ? 'Up to 2 Channels' : 'Up to 100 Channels'}
            </StyledUppercaseText>
          </Box>
        </Box>
        <Box direction={small ? 'column' : 'row'} gap="1rem">
          {renderAccountTypeOptions()}
        </Box>
      </Box>
    </Box>
  );
};

ServiceSelectionStep.propTypes = {
  small: PropTypes.bool.isRequired,
  offerings: PropTypes.shape({
    useCid: PropTypes.bool,
    useSocial: PropTypes.bool,
    useWatermarking: PropTypes.bool,
  }).isRequired,
  setOfferings: PropTypes.func.isRequired,
  accountKind: PropTypes.oneOf(['creator', 'agency']).isRequired,
  setAccountKind: PropTypes.func.isRequired,
};

export default ServiceSelectionStep;
